import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { UserInterface } from "~/api/user/interfaces";
import { getCurrentUser, updateCurrentUser } from "~/api/user/user";

export const useUserStore = defineStore(
  "user",
  () => {
    const user = ref<UserInterface | null>(null);
    const isLoading = ref(false);

    const isLoggedIn = computed(() => !!user.value);
    const fullName = computed(() => {
      if (!user.value) return "";
      return `${user.value.first_name || ""} ${
        user.value.last_name || ""
      }`.trim();
    });

    // Single-flight promise to avoid concurrent identical network requests
    let currentFetchUserPromise: Promise<UserInterface | null> | null = null;

    const fetchUser = async (options?: {
      showToast?: boolean;
      toastMessage?: string;
      toastParams?: Record<string, unknown>;
    }) => {
      const { setLocale } = useI18n();

      // If we already have the user loaded, return it immediately
      if (user.value) return user.value;

      // If a fetch is already in progress, wait for it
      if (currentFetchUserPromise) return await currentFetchUserPromise;

      isLoading.value = true;
      currentFetchUserPromise = (async () => {
        try {
          const fetched = await getCurrentUser(undefined, options);
          user.value = fetched;

          if (fetched.lang) {
            try {
              await setLocale(fetched.lang);
            } catch {
              // Best-effort: non blocca il fetch
            }
          }

          return fetched;
        } catch (error) {
          console.error("Error fetching user:", error);
          return null;
        } finally {
          isLoading.value = false;
          currentFetchUserPromise = null;
        }
      })();

      return await currentFetchUserPromise;
    };

    const updateUser = async (
      updatedUserData: Partial<UserInterface>,
      options?: {
        showToast?: boolean;
        toastMessage?: string;
        toastErrorMessage?: string;
        toastParams?: Record<string, unknown>;
      },
    ) => {
      isLoading.value = true;
      try {
        const updatedUser = await updateCurrentUser(
          updatedUserData,
          undefined,
          options,
        );
        user.value = updatedUser;
        return updatedUser;
      } catch (error) {
        console.error("Error updating user:", error);
        throw error;
      } finally {
        isLoading.value = false;
      }
    };

    const clearUser = () => {
      user.value = null;
    };

    return {
      user,
      isLoading,
      isLoggedIn,
      fullName,
      fetchUser,
      updateUser,
      clearUser,
    };
  },
  {
    persist: {
      storage: localStorage,
    },
  },
);
